"use client";

import { useCallback } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { isThetaError } from "@theta/api-client";
import { MAX_PAGE_LIMIT } from "../api/theta";
import {
  acceptRequest,
  addMessageReaction,
  addParticipants,
  declineRequest,
  deleteMessage,
  editMessage,
  getConversation,
  getMessageReactors,
  getMessages,
  leaveConversation,
  markConversationRead,
  markConversationUnread,
  removeMessageReaction,
  renameConversation,
  sendMessage,
  setMuted,
  type Conversation,
  type CreateMessageInput,
  type Message,
  type MessageReaction,
  type Participation,
} from "../api/messages";
import { conversationsKey, meKey } from "./useConversations";
import { userMessagingKey } from "./useUserMessaging";

/** The open conversation's own document. */
export function conversationKey(id: number) {
  return ["v1", "conversation", id] as const;
}

/** Its messages — the pollable half, and the only thing polled at 1 Hz. */
export function conversationMessagesKey(id: number) {
  return ["v1", "conversation-messages", id] as const;
}

/** Retrying a 404 is retrying a correct answer: a non-participant IS told 404 (D7). */
export function retryUnlessNotFound(failureCount: number, error: unknown) {
  return !isThetaError(error, "notFound") && failureCount < 2;
}

/**
 * One conversation: roster, shared groups with tenure, read state, `can`.
 *
 * Not polled by design (§2.14): the 1 Hz poll targets `/messages`, and
 * everything here changes when somebody joins, leaves, mutes or accepts — all
 * writes this app makes, so the mutations below splice their answers back in.
 *
 * Replaces `useConversationDetails`: `/details` is folded in as `sharedGroups`,
 * so the details pane costs no request of its own.
 *
 * `refetchMs` is for the one surface that reads this document with no thread
 * open under it — a minimized chat window, whose unread badge is all that tells
 * the viewer a message arrived. It used to ride the 1 Hz poll this document no
 * longer has; a slow tick keeps the badge alive at a fifteenth of the requests.
 */
export function useConversation(id: number, options: { refetchMs?: number } = {}) {
  const { refetchMs } = options;
  return useQuery({
    queryKey: conversationKey(id),
    queryFn: () => getConversation(id),
    enabled: id > 0,
    staleTime: 60 * 1000,
    retry: retryUnlessNotFound,
    refetchInterval: () =>
      refetchMs !== undefined &&
      typeof document !== "undefined" &&
      document.visibilityState !== "hidden"
        ? refetchMs
        : (false as const),
    refetchIntervalInBackground: false,
  });
}

/** The cadence to use until the server has said otherwise (D12). */
const FALLBACK_POLL_MS = 1_000;

/**
 * The thread is a tiling of anchored ranges, all of which stay live.
 *
 * What a reader looks at is not "the newest 50 messages" — it is the newest
 * window plus however many older pages they pressed "Hent ældre beskeder" for.
 * §4.1's claim about `/messages` is that each is addressable as a stable range:
 * `?after=<id>&limit=<n>` names the same messages every time, where the legacy
 * `?limit=50` named a window that slid forward under the caller. So each
 * `Segment` is one anchored range with its own `state` token, anchored on the
 * last id of the tile before it — together covering the screen with no hole.
 *
 * Two properties fall out, and the old code had neither:
 *
 *  1. A poll cannot skip a message. The live tile is re-read as "everything
 *     after the id I hold", so a burst longer than one window between ticks —
 *     a tab hidden over lunch, where the poll is paused — comes back as
 *     `hasMore` and is paged forward (`catchUp`) instead of arriving as a
 *     window floating above what we hold. That retires `absorbWindow`'s
 *     successor, ConversationThread's `gap` branch, which "repaired" the hole
 *     by discarding the whole loaded thread — every older page included.
 *
 *  2. An older page stays alive. Every DM attachment is protected tier, so
 *     every media URL a message carries is signed and expires in an hour or
 *     two. The server folds the payload's deadline into its `state` token
 *     (`stateWithMedia`, which declares itself stale `refreshLead` seconds
 *     early) — but a token speaks only for the page it was minted with. A poll
 *     covering only the newest window refreshes only that window's signatures,
 *     and photos two pages up die where they sit, `loading="lazy"` and not yet
 *     requested, so scrolling back hits a wall of broken images that reads as a
 *     server bug. Every tile is re-read on its own token.
 *
 * Cost is bounded at two conditional requests per tick whatever is loaded: the
 * live tile every tick plus one older tile, swept round-robin. A lap takes
 * (tiles - 1) ticks, and the floor on `refreshLead` at the production window is
 * 450 s — so a tile could only miss its deadline past some four hundred loaded
 * pages, far more than the thread would render.
 */
interface Segment {
  /**
   * The `?after=` this tile is read with: it holds exactly the messages with
   * a larger id, up to `limit` of them.
   *
   * `null` only before the first read has landed — the contract's one
   * unanchored arm, for the one moment there is no id to anchor to.
   */
  anchor: number | null;
  /** This tile's `?ifChanged=`. Its deadline is its own, not the thread's. */
  state: string;
  /** Ascending by id, as every arm of the route answers. */
  items: Message[];
  /** The page before this tile. Only the FRONT tile's is ever read. */
  prevCursor: string | null;
  /** Is anything newer than this tile's last row? Drives `catchUp`. */
  hasMore: boolean;
}

interface ThreadStore {
  segments: Segment[];
  /** Which older tile the next tick sweeps. */
  sweep: number;
  /** The last published view, so an unchanged tick re-renders nothing. */
  view: ThreadView;
  /** The cadence the server last asked for (D12). */
  pollAfterMs: number | null;
  /** One "Hent ældre beskeder" in flight at a time, across every host. */
  loadingOlder: boolean;
}

/** What the thread renders from. */
interface ThreadView {
  /** Every message held, ascending by id, unique by id. */
  messages: Message[];
  /** The page before the oldest message held, or null when there is none. */
  frontCursor: string | null;
}

const EMPTY_VIEW: ThreadView = { messages: [], frontCursor: null };

/**
 * The tiling is per query key, not per component, because one conversation can
 * be on screen twice at once: `ChatDock` is mounted by the root layout, so a
 * docked window and `/beskeder/{id}` for the same thread coexist and the dock
 * survives the navigation that opens the page. Both mount
 * `useConversationThread(id)` as observers of one react-query key.
 *
 * Held per component, whichever instance's `queryFn` react-query happened to
 * run would decide what the shared cache entry says, against a tiling the other
 * knows nothing about — so "Hent ældre beskeder" in the dock would load history
 * the page dropped on its next tick, and in the instance that does not own the
 * queryFn it would do nothing at all.
 *
 * Keyed weakly by QueryClient rather than by module: that is react-query's own
 * scope for "one app's data", it is what a test constructs per case, and the
 * store dies with it instead of leaking between cases.
 */
const THREAD_STORES = new WeakMap<QueryClient, Map<number, ThreadStore>>();

/**
 * How many conversations keep their loaded history after the reader leaves.
 *
 * Retained rather than dropped on unmount because clicking back into a thread
 * is the common move and its history is the costliest thing to re-read;
 * bounded because every retained tiling holds its messages. Tiles come back
 * stale and the following ticks refresh them — a tile's token carries its own
 * deadline, so "stale" is something it reports rather than something tracked
 * here.
 *
 * Comfortably above the most that can be mounted at once (MAX_DOCKS = 3 plus
 * the open page), so eviction only reaches a thread nobody is looking at.
 * Evicting a mounted one would not corrupt anything — the next tick would find
 * an empty store and re-read the newest window — but it would throw away that
 * reader's loaded history for nothing.
 */
const MAX_RETAINED_THREADS = 8;

function threadStores(client: QueryClient): Map<number, ThreadStore> {
  let stores = THREAD_STORES.get(client);
  if (!stores) {
    stores = new Map();
    THREAD_STORES.set(client, stores);
  }
  return stores;
}

function threadStore(client: QueryClient, id: number): ThreadStore {
  const stores = threadStores(client);
  const existing = stores.get(id);
  if (existing) {
    // Re-insert, so iteration order is least-recently-used first.
    stores.delete(id);
    stores.set(id, existing);
    return existing;
  }
  const created: ThreadStore = {
    segments: [],
    sweep: 0,
    view: EMPTY_VIEW,
    pollAfterMs: null,
    loadingOlder: false,
  };
  stores.set(id, created);
  for (const oldest of stores.keys()) {
    if (stores.size <= MAX_RETAINED_THREADS) break;
    stores.delete(oldest);
  }
  return created;
}

/**
 * A burst can be arbitrarily long, but a poll tick may not be: this bounds one
 * tick's catch-up so a thread that somehow always reports `hasMore` cannot
 * spin. Anything past it is picked up by the next tick.
 */
const MAX_CATCHUP_PAGES = 20;

/** Same messages, same objects, in the same order. */
function sameMessages(a: Message[], b: Message[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * One conversation's thread: everything the reader has loaded, kept live.
 *
 * Returns the messages plus the two things only the owner of the tiling can
 * do — fetch the page before the front tile, and splice a reaction into a
 * message that may be in ANY tile, including one no poll would reach.
 *
 * `limit` is the tile size, and it is a request rather than a promise: the
 * route caps it (`MAX_PAGE_LIMIT`) and `lib/api/messages.ts` clamps it there.
 */
export function useConversationThread(id: number, limit = 50) {
  const queryClient = useQueryClient();
  // Looked up on every render rather than held in a ref: the store belongs to
  // the conversation, not to this component, so changing `id` simply reaches a
  // different one — no reset, and no frame in which the previous
  // conversation's messages sit under the new conversation's header.
  const store = threadStore(queryClient, id);

  /** Has this store been evicted (or the route changed) mid-request? */
  const current = useCallback(
    (candidate: ThreadStore) => threadStores(queryClient).get(id) === candidate,
    [id, queryClient],
  );

  /**
   * Flatten the tiling into what renders.
   *
   * Tiles overlap by design at their seams (a deletion inside one lets its
   * window slide forward into the next), so the LATER tile wins — it is the
   * one the poll refreshes most often, and therefore the one whose signed
   * media is freshest. Returning the previous object when nothing moved is
   * what keeps an `unchanged` tick from re-rendering the thread.
   */
  const publish = useCallback((target: ThreadStore): ThreadView => {
    const byId = new Map<number, Message>();
    for (const segment of target.segments) {
      for (const message of segment.items) byId.set(message.id, message);
    }
    const messages = [...byId.values()].sort((a, b) => a.id - b.id);
    const frontCursor = target.segments[0]?.prevCursor ?? null;
    const previous = target.view;
    if (
      previous.frontCursor === frontCursor &&
      sameMessages(previous.messages, messages)
    ) {
      return previous;
    }
    const next: ThreadView = { messages, frontCursor };
    target.view = next;
    return next;
  }, []);

  /**
   * Re-read one tile on its own token.
   *
   * `unchanged` means both that nothing in the conversation moved and that
   * this tile's signatures are good for another `refreshLead`. The token
   * answers both at once, which is why a tile nobody writes in still comes
   * back with a document before its images die.
   */
  const refresh = useCallback(
    async (target: ThreadStore, index: number): Promise<boolean> => {
      const segment = target.segments[index];
      const result = await getMessages(id, {
        ...(segment.anchor === null ? {} : { after: segment.anchor }),
        limit,
        ...(segment.state ? { ifChanged: segment.state } : {}),
      });
      target.pollAfterMs = result.pollAfterMs;
      if (!current(target)) return false;
      if (result.unchanged) {
        if (result.state) segment.state = result.state;
        return false;
      }
      // Written back by identity, not by the index we started with: "Hent
      // ældre beskeder" unshifts a tile onto the front and can land while this
      // request is in flight, after which `index` names the tile before this
      // one and the live tile's page would overwrite the page just fetched.
      const at = target.segments.indexOf(segment);
      if (at === -1) return false;
      const page = result.data;
      const empty = page.items.length === 0;
      target.segments[at] = {
        // An emptied tile keeps the anchor and the cursor it was built with:
        // they still name a position, and the alternative is retiring "Hent
        // ældre beskeder" because a page was deleted out from under it.
        anchor: segment.anchor ?? (empty ? null : page.items[0].id - 1),
        state: result.state ?? "",
        items: page.items,
        prevCursor: empty ? segment.prevCursor : page.prevCursor,
        hasMore: page.hasMore,
      };
      return true;
    },
    [current, id, limit],
  );

  /**
   * Page forward from the live tile until nothing is newer.
   *
   * This is what makes "the poll cannot skip a message" true rather than
   * hoped-for: each new tile is anchored on the last id of the one before it,
   * so the tiling stays gapless however long the tab was hidden. A tile only
   * starts when the one before it is full, so a steadily-chatting thread grows
   * one tile per `limit` messages, not one per message.
   */
  const catchUp = useCallback(
    async (target: ThreadStore): Promise<boolean> => {
      let changed = false;
      for (let i = 0; i < MAX_CATCHUP_PAGES; i++) {
        const tail = target.segments[target.segments.length - 1];
        if (!tail?.hasMore) break;
        const from =
          tail.items.length > 0 ? tail.items[tail.items.length - 1].id : tail.anchor;
        if (from === null) break;
        const result = await getMessages(id, { after: from, limit });
        target.pollAfterMs = result.pollAfterMs;
        if (!current(target) || result.unchanged) break;
        if (result.data.items.length === 0) {
          tail.hasMore = false;
          break;
        }
        target.segments.push({
          anchor: from,
          state: result.state ?? "",
          items: result.data.items,
          prevCursor: result.data.prevCursor,
          hasMore: result.data.hasMore,
        });
        changed = true;
      }
      return changed;
    },
    [current, id, limit],
  );

  const query = useQuery({
    queryKey: conversationMessagesKey(id),
    queryFn: async (): Promise<ThreadView> => {
      const target = threadStore(queryClient, id);

      if (target.segments.length === 0) {
        // The contract's one unanchored read, and the one moment it is right:
        // the first request of a session, with no id to anchor to. Everything
        // after this is `?after=`.
        const result = await getMessages(id, { limit });
        target.pollAfterMs = result.pollAfterMs;
        if (!current(target) || result.unchanged) return publish(target);
        target.segments = [
          {
            anchor:
              result.data.items.length > 0 ? result.data.items[0].id - 1 : null,
            state: result.state ?? "",
            items: result.data.items,
            prevCursor: result.data.prevCursor,
            hasMore: result.data.hasMore,
          },
        ];
        await catchUp(target);
        return publish(target);
      }

      // The live tile, every tick.
      let changed = await refresh(target, target.segments.length - 1);
      if (await catchUp(target)) changed = true;

      // And one older tile, swept round-robin — one extra conditional request
      // per tick however much history is loaded. Their signatures expire on
      // their own clocks; the live tile's token says nothing about them.
      const older = target.segments.length - 1;
      if (older > 0) {
        const index = target.sweep % older;
        target.sweep = index + 1;
        if (await refresh(target, index)) changed = true;
      }

      return changed ? publish(target) : target.view;
    },
    enabled: id > 0,
    retry: retryUnlessNotFound,

    // The server owns the cadence (D12): changing it is a backend deploy, not
    // an App Store release. The visibility check is what actually pauses a
    // backgrounded tab — in TanStack v5 `refetchIntervalInBackground: false`
    // alone does not — so the two must stay together.
    refetchInterval: () =>
      typeof document !== "undefined" && document.visibilityState !== "hidden"
        ? (threadStores(queryClient).get(id)?.pollAfterMs ?? FALLBACK_POLL_MS)
        : (false as const),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  });

  /**
   * "Hent ældre beskeder": the page before the front tile, which becomes the
   * new front tile — anchored, tokened and swept like every other one, rather
   * than a frozen array nothing ever reads again.
   */
  const loadOlder = useCallback(async (): Promise<void> => {
    const target = threadStore(queryClient, id);
    const front = target.segments[0];
    if (!front?.prevCursor || target.loadingOlder) return;
    target.loadingOlder = true;
    try {
      // The cursor carries its own direction (§1.3): this one goes backwards.
      const result = await getMessages(id, { cursor: front.prevCursor, limit });
      target.pollAfterMs = result.pollAfterMs;
      // The store may have been evicted while this request was in flight.
      if (!current(target) || result.unchanged) return;
      if (result.data.items.length === 0) {
        // The server says there was nothing there after all; retire the button
        // rather than leave it offering a page that does not exist.
        front.prevCursor = null;
      } else {
        target.segments.unshift({
          anchor: result.data.items[0].id - 1,
          state: result.state ?? "",
          items: result.data.items,
          prevCursor: result.data.prevCursor,
          hasMore: result.data.hasMore,
        });
        target.sweep = 0;
      }
      queryClient.setQueryData(conversationMessagesKey(id), publish(target));
    } finally {
      target.loadingOlder = false;
    }
  }, [current, id, limit, publish, queryClient]);

  /**
   * Splice the authoritative pill list a reaction answered with.
   *
   * Not an invalidation — the reacted message can be in any tile and a
   * reaction does not move the tiling — and not a recomputation: the server
   * sent the whole summary.
   */
  const patchReactions = useCallback(
    (messageId: number, reactions: MessageReaction[]) => {
      const target = threadStore(queryClient, id);
      let hit = false;
      target.segments = target.segments.map((segment) => {
        if (!segment.items.some((m) => m.id === messageId)) return segment;
        hit = true;
        return {
          ...segment,
          items: segment.items.map((m) =>
            m.id === messageId ? { ...m, reactions } : m,
          ),
        };
      });
      if (hit) {
        queryClient.setQueryData(conversationMessagesKey(id), publish(target));
      }
    },
    [id, publish, queryClient],
  );

  const view = query.data ?? store.view;
  return {
    messages: view.messages,
    frontCursor: view.frontCursor,
    error: query.error,
    isLoading: query.isLoading,
    loadOlder,
    patchReactions,
  };
}

/**
 * Every surface a write to one conversation touches: its own document, the
 * inbox row, the header badge on `GET /me`, and "does a 1:1 with this person
 * exist, and is it still a request?"
 *
 * The requests tab is no longer a fourth key — inbox and requests are one
 * collection (`?state=` filters it), so the row moving out of "Anmodninger" is
 * the same invalidation as the row changing.
 *
 * `userMessagingKey` is the fourth because accepting flips `isRequest`,
 * declining removes the thread for good, and leaving removes your seat — and
 * `/beskeder/ny` reads all three off that one cached answer to decide whether
 * to offer a composer at all. These writes know a conversation id and not the
 * other person's user id, which is why the whole prefix goes.
 */
function useConversationInvalidator() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: conversationsKey });
    queryClient.invalidateQueries({ queryKey: meKey });
    queryClient.invalidateQueries({ queryKey: userMessagingKey });
  };
}

/** Splice a server answer into the cached conversation. Never recompute one. */
function useConversationPatch(id: number) {
  const queryClient = useQueryClient();
  return (next: Partial<Conversation>) => {
    queryClient.setQueryData<Conversation>(conversationKey(id), (old) =>
      old ? { ...old, ...next } : old,
    );
  };
}

/**
 * Send.
 *
 * Replying to a request accepts it server-side, and the server says so in
 * `meta.sideEffects.conversationAccepted` — so the conversation document is
 * re-read exactly when that happened, not on every send just in case.
 */
export function useSendMessage(id: number) {
  const queryClient = useQueryClient();
  const invalidate = useConversationInvalidator();
  return useMutation({
    mutationFn: (body: CreateMessageInput) => sendMessage(id, body),
    onSuccess: ({ accepted }) => {
      queryClient.invalidateQueries({ queryKey: conversationMessagesKey(id) });
      if (accepted) {
        queryClient.invalidateQueries({ queryKey: conversationKey(id) });
      }
      invalidate();
    },
  });
}

/**
 * Move the read marker. The server answers with the new unread count, so there
 * is nothing to guess: the 20-line optimistic patch and its rollback are gone
 * with the endpoint that made them necessary.
 */
export function useMarkConversationRead(id: number) {
  const patch = useConversationPatch(id);
  const invalidate = useConversationInvalidator();
  return useMutation({
    mutationFn: (throughMessageId?: Message["id"]) =>
      markConversationRead(id, throughMessageId),
    onSuccess: (unreadCount) => {
      patch({ unreadCount });
      invalidate();
    },
  });
}

/**
 * OSO-520 — the same three lines as above with the marker going the other way,
 * so the inbox row, the header badge and the thread all learn about it from
 * the one invalidator every other write here uses.
 *
 * Safe to call from the open thread, where the menu lives: the thread's
 * mark-read effect fires on a newest id it has not seen before, so it does not
 * re-run on the id it just marked, and a member who un-reads a thread and
 * leaves it keeps that. A message arriving afterwards marks it read again,
 * which is right — they were still looking.
 */
export function useMarkConversationUnread(id: number) {
  const patch = useConversationPatch(id);
  const invalidate = useConversationInvalidator();
  return useMutation({
    mutationFn: () => markConversationUnread(id),
    onSuccess: (unreadCount) => {
      patch({ unreadCount });
      invalidate();
    },
  });
}

/** The fields a `Participation` and a `Conversation` share, spliced across. */
function seatOf(seat: Participation): Partial<Conversation> {
  return {
    state: seat.state,
    isRequest: seat.isRequest,
    muted: seat.muted,
    unreadCount: seat.unreadCount,
    lastReadMessageId: seat.lastReadMessageId,
    can: seat.can,
  };
}

/** Mute or unmute. The badge on `GET /me` changes; the inbox count does not. */
export function useMuteConversation(id: number) {
  const patch = useConversationPatch(id);
  const invalidate = useConversationInvalidator();
  return useMutation({
    mutationFn: (muted: boolean) => setMuted(id, muted),
    onSuccess: (seat) => {
      patch(seatOf(seat));
      invalidate();
    },
  });
}

/** The three writes that answer with the whole conversation. */
function useConversationWrite(id: number, write: () => Promise<Conversation>) {
  const queryClient = useQueryClient();
  const invalidate = useConversationInvalidator();
  return useMutation({
    mutationFn: write,
    onSuccess: (conversation) => {
      queryClient.setQueryData(conversationKey(id), conversation);
      invalidate();
    },
  });
}

export function useLeaveConversation(id: number) {
  return useConversationWrite(id, () => leaveConversation(id));
}

/** Accepting reveals the thread; the row stays in the inbox, only `can` moves. */
export function useAcceptRequest(id: number) {
  return useConversationWrite(id, () => acceptRequest(id));
}

/** Declining hides it for good. The sender is never told. */
export function useDeclineRequest(id: number) {
  return useConversationWrite(id, () => declineRequest(id));
}

/**
 * Name the conversation, or clear the name with `null`.
 *
 * Answers with the whole conversation, so the label is spliced in rather than
 * refetched — this document is not polled, and header, inbox row and details
 * pane read it from the same cache entry. `invalidate()` carries the change to
 * the inbox list, whose five-second poll notices it through the fingerprint.
 */
export function useRenameConversation(id: number) {
  const queryClient = useQueryClient();
  const invalidate = useConversationInvalidator();
  return useMutation({
    mutationFn: (name: string | null) => renameConversation(id, name),
    onSuccess: (conversation) => {
      queryClient.setQueryData(conversationKey(id), conversation);
      invalidate();
    },
  });
}

export function useAddParticipants(id: number) {
  const queryClient = useQueryClient();
  const invalidate = useConversationInvalidator();
  return useMutation({
    mutationFn: (userIds: Conversation["participants"][number]["user"]["id"][]) =>
      addParticipants(id, userIds),
    onSuccess: (conversation) => {
      queryClient.setQueryData(conversationKey(id), conversation);
      invalidate();
    },
  });
}

/**
 * React to one message.
 *
 * An upsert and a delete, not a toggle (D10b), so `mine` — which the server
 * put on the pill — decides which. The answer is the authoritative pill list
 * and the caller splices it in: a reacted message can be in a page older than
 * the polled window, where invalidating a query would not reach it.
 */
export function useReactToMessage() {
  return useMutation({
    mutationFn: ({
      messageId,
      emoji,
      mine,
    }: {
      messageId: number;
      emoji: string;
      mine: boolean;
    }): Promise<MessageReaction[]> =>
      mine
        ? removeMessageReaction(messageId, emoji)
        : addMessageReaction(messageId, emoji),
  });
}

/**
 * Withdraw one of my own messages — OSO-564.
 *
 * NOTHING IS SPLICED OUT LOCALLY. The thread's tiles are re-read whole on the
 * poll's own conditional token, and a deletion moves that token (the
 * fingerprint counts LIVE rows and carries `max(deleted_at)` behind it), so
 * the row leaves within a tick — for every reader, not only this one. Removing
 * it from the store here as well would be a second, hand-maintained answer to
 * a question the poll already answers correctly.
 *
 * The INBOX is invalidated, because it is not on the 1 Hz poll: deleting the
 * newest message promotes the previous one into the row's excerpt, and waiting
 * out the 5 s inbox poll to show that is a visibly stale line beside a thread
 * the reader just changed.
 */
export function useDeleteMessage() {
  const invalidate = useConversationInvalidator();
  return useMutation({
    mutationFn: (messageId: number) => deleteMessage(messageId),
    onSuccess: invalidate,
  });
}

/**
 * Rewrite one of my own messages — OSO-607.
 *
 * SAME SHAPE, SAME REASONING AS `useDeleteMessage` ABOVE. Nothing is spliced
 * into the thread here: the poll's conditional token carries
 * `max(updated_at)`, which an edit moves, so the new words reach every reader
 * within a tick. The caller gets the server's `Message` back for the one thing
 * the poll cannot do fast enough — repainting the bubble the writer is looking
 * at, without a second of the old text sitting under a "Gem" they just pressed.
 *
 * The inbox is invalidated for the same reason a deletion invalidates it: the
 * newest message's excerpt is the row's line, and it is not on the 1 Hz poll.
 */
export function useEditMessage() {
  const invalidate = useConversationInvalidator();
  return useMutation({
    mutationFn: (input: { messageId: number; bodyMarkdown: string }) =>
      editMessage(input.messageId, input.bodyMarkdown),
    onSuccess: invalidate,
  });
}

/**
 * Who reacted — fetched only while `enabled` (on hover/tap), since a thread
 * can hold hundreds of reacted messages nobody looks closely at.
 */
export function useMessageReactors(messageId: number, enabled: boolean) {
  return useQuery({
    queryKey: ["v1", "message-reactors", messageId],
    // `limit` MATTERS HERE. The legacy endpoint shipped the reactor list as
    // one unpaged array; `GET /messages/{id}/reactions` is cursor-paged and
    // its default page is 20, so a message with more reactions than that came
    // back silently truncated and the tooltip counted the names it happened to
    // receive. This asks for the route's maximum, and the caller reads the
    // authoritative `count` on the pills for the total rather than the length
    // of what arrived.
    queryFn: () => getMessageReactors(messageId, { limit: MAX_PAGE_LIMIT }),
    enabled,
    staleTime: 30 * 1000,
  });
}
